/**
 * Enum of all feature gate keys used in oauth-consent.
 *
 * Each entry should include a comment linking to the Switcheroo gate URL.
 * Example:
 *   // https://switcheroo.atlassian.com/ui/gates/<gate-id>/key/<gate-key>
 *   MY_FEATURE = 'oauth-consent-my-feature',
 */
export enum OAuthConsentFeatureGate {
  // Limits Rovo consent to one selected site; disabled preserves multi-site selection.
  // https://switcheroo.atlassian.com/ui/gates/26308305-524d-4fc6-8a96-90c232718d8f/key/oauth-consent_rovo-single-site
  ROVO_SINGLE_SITE = 'oauth-consent_rovo-single-site',

  // Gates Trello consent screen access to app authors/collaborators (Horsefly program).
  // https://switcheroo.atlassian.com/ui/gates/7d71dd9e-26b8-4587-b202-8ef2814277a1/key/oauth-consent_trello-contributor-gate
  TRELLO_CONTRIBUTOR_GATE = 'oauth-consent_trello-contributor-gate',

  // Internal bypass for site selector when /consent/info returns no workspaces (e.g. timeout).
  // NOT intended for production use — shown only to internal users via this gate.
  SITE_SELECTOR_BYPASS = 'oauth-consent_site-selector-bypass',

  // Sorts the workspace list in MCP-style site selectors.
  MCP_SORT_WORKSPACES = 'oauth-consent_mcp-sorted-workspaces',

  // Enables selecting more than one Trello workspace on the MCP consent screen.
  TRELLO_MCP_MULTI_WORKSPACE_SELECTION = 'oauth-consent_trello-mcp-multi-workspace',

  // Enables the Trello workspace selector alongside the Atlassian site selector
  // in mixed-scope MCP consent flows. When disabled, the Trello workspace
  // selector is hidden even if hasTrelloProductAccess is true and Trello scopes
  // are present.
  TRELLO_MCP_MIXED_WORKSPACE = 'oauth-consent_trello-mcp-mixed-workspace',

  // Enables the reworked Trello multi-workspace selector on the MCP consent
  // screen, and the multi-workspace data and selection rules behind it.
  MULTI_WORKSPACE_SELECTOR = 'oauth-consent_multi-workspace-selector',

  // Shows the Jira "Delete" intent group on the MCP permissions list. Owned by
  // the MCP service, so the key does not follow the oauth-consent_ convention.
  MCP_JIRA_DELETE_TOOLSETS = 'atlassian_mcp_jira_delete_toolsets',

  // Shows the Jira "Manage" intent group on the MCP permissions list. Owned by
  // the MCP service, so the key does not follow the oauth-consent_ convention.
  MCP_JIRA_MANAGE_TOOLSETS = 'atlassian_mcp_jira_admin_toolsets',
  // Redirects the consent tab to the dagterminal success screen after Accept
  // when the OAuth callback uses the rovo:// custom scheme. The system browser
  // hands the rovo:// URI off to the OS to launch the native app, but the tab
  // itself has nowhere to navigate and would otherwise remain on the stale
  // consent screen.
  // https://switcheroo.atlassian.com/ui/gates/<id>/key/oauth-consent_rovo-success-redirect
  ROVO_SUCCESS_REDIRECT = 'oauth-consent_rovo-success-redirect',
}
